// Renders an accurate, framing-correct thumbnail for an adjusted photo.
//
// This mirrors the backend print pipeline (see backend/imaging.py `generate_print`)
// exactly so the thumbnail shows the SAME framing/rotation that will be printed:
//   1. rotate the image by `-rotation` (expand canvas), then
//   2. crop {x, y, width, height} (in the rotated PREVIEW-pixel space), then
//   3. scale that region into a square.
//
// crop is `croppedAreaPixels` from react-easy-crop, already in preview pixels of
// the rotated bounding box — identical to what the server consumes (ratio = 1 here
// because we operate on the same preview image).
export function renderAdjustedThumb(src, crop, rotation = 0) {
  return new Promise((resolve, reject) => {
    if (!crop || !crop.width || !crop.height) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous"; // preview is same-origin; harmless if not
    img.onload = () => {
      try {
        const r = (((Number(rotation) || 0) % 360) + 360) % 360;
        const rad = (r * Math.PI) / 180;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const swap = r === 90 || r === 270;
        const cw = swap ? h : w;
        const ch = swap ? w : h;

        // Rotated image canvas (matches PIL im.rotate(-rotation, expand=True))
        const rc = document.createElement("canvas");
        rc.width = Math.max(1, Math.round(cw));
        rc.height = Math.max(1, Math.round(ch));
        const rctx = rc.getContext("2d");
        rctx.translate(rc.width / 2, rc.height / 2);
        rctx.rotate(rad);
        rctx.drawImage(img, -w / 2, -h / 2);

        // Crop region (clamped) then scale into a square output
        let sx = crop.x, sy = crop.y, sw = crop.width, sh = crop.height;
        sx = Math.max(0, Math.min(sx, rc.width - 1));
        sy = Math.max(0, Math.min(sy, rc.height - 1));
        sw = Math.max(1, Math.min(sw, rc.width - sx));
        sh = Math.max(1, Math.min(sh, rc.height - sy));

        const SIZE = 320;
        const out = document.createElement("canvas");
        out.width = SIZE;
        out.height = SIZE;
        const octx = out.getContext("2d");
        octx.drawImage(rc, sx, sy, sw, sh, 0, 0, SIZE, SIZE);
        resolve(out.toDataURL("image/jpeg", 0.9));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = src;
  });
}
